import { useRef, useState, useMemo, useEffect } from 'react';
import { type MRT_ColumnDef } from 'material-react-table';
import { GeneralTable, SingleTableBoxScene } from '@/shared/components/ui';
import DateRangePicker from '@/shared/components/ui/CustomSearch/DateRangePicker';
import {
  Grid,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Divider,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Search,
  FileDownload,
  Visibility,
  CheckCircle,
  Close,
  CloudUpload,
  Delete,
  PictureAsPdf,
  Description,
  TableChart,
  Image as ImageIcon,
  InsertDriveFile,
  Download,
  ArrowUpward,
  ArrowDownward,
  AttachMoney,
  FilterAltOff,
  Schedule,
  History,
  Email,
  Add,
  Done,
  Cancel,
} from '@mui/icons-material';
import Flatpickr from 'react-flatpickr';
import {
  useFetchTicketsSoporteCorporativo,
  useUploadDocumentosTicket,
  useExportTicketsToExcel,
  useUpdateTicketSoporteCorporativo,
  useFetchAuditoriaTicketSoporte,
  useRechazarTicketSoporteCorporativo,
  useUpdateCorreosCopiaTicket,
  useFetchCorreosPredeterminados,
  useUpdateCorreosPredeterminados,
} from '@/shared/actions/erp';
import { useNavigate } from 'react-router-dom';
import { ITicketSoporteCorporativo, IDocumentoTicket, TipoAccionAuditoria } from '@/shared/interfaces/erp/soporte-corporativo';
import { hasPermission, useTableServerSideFiltering } from '@/shared';
import { PermissionsEnum } from '@/auth/interfaces';
import { type MRT_RowSelectionState } from 'material-react-table';

// Función para formatear fecha
const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-EC', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Función para formatear fecha y hora
const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('es-EC', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// Función para formatear fecha a YYYY-MM-DD en zona horaria local (evita problemas con UTC)
const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper para obtener icono según tipo de documento
const getDocIcon = (tipo: string) => {
  switch (tipo) {
    case 'image':
      return <ImageIcon sx={{ color: '#4CAF50' }} />;
    case 'pdf':
      return <PictureAsPdf sx={{ color: '#F44336' }} />;
    case 'word':
      return <Description sx={{ color: '#2196F3' }} />;
    case 'excel':
      return <TableChart sx={{ color: '#4CAF50' }} />;
    default:
      return <InsertDriveFile sx={{ color: '#9E9E9E' }} />;
  }
};

// Formatear tamaño de archivo
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};


// Configuración de tipos de acción para auditoría
const AUDIT_ACTION_CONFIG: Record<TipoAccionAuditoria, { label: string; color: string; bgColor: string }> = {
  CREACION: { label: 'Creación', color: '#1976d2', bgColor: '#e3f2fd' },
  FINALIZACION: { label: 'Finalización', color: '#2e7d32', bgColor: '#e8f5e9' },
  EXPORTACION_EXCEL: { label: 'Exportación Excel', color: '#9c27b0', bgColor: '#f3e5f5' },
  ENVIO_EMAIL: { label: 'Envío Email', color: '#ed6c02', bgColor: '#fff3e0' },
  SUBIDA_DOCUMENTO: { label: 'Subida Documento', color: '#0288d1', bgColor: '#e1f5fe' },
  ELIMINACION_DOCUMENTO: { label: 'Eliminación Documento', color: '#d32f2f', bgColor: '#ffebee' },
  RECHAZO: { label: 'Rechazo', color: '#c62828', bgColor: '#ffcdd2' },
  ACTUALIZACION_CORREOS_COPIA: { label: 'Actualización Correos', color: '#5c6bc0', bgColor: '#e8eaf6' },
  ACTUALIZACION_CORREOS_PREDETERMINADOS: { label: 'Config. Correos Predeterminados', color: '#7b1fa2', bgColor: '#f3e5f5' },
};

export default function GestionTicketsPage() {
  const navigate = useNavigate();
  const flatpickrRef = useRef<Flatpickr | null>(null);
  const finalizarFileInputRef = useRef<HTMLInputElement>(null);
  const [dateRange, setDateRange] = useState<Date[]>([]);
  const [numeroTicket, setNumeroTicket] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [searchParams, setSearchParams] = useState<{
    fecha_ejecucion_after?: string;
    fecha_ejecucion_before?: string;
    numero_referencia?: string;
  }>({});
  // Filtros por columna (server-side): mapea id de columna -> parámetro del backend con debounce
  const { columnFilters, setColumnFilters, filterObject } = useTableServerSideFiltering();

  // Modal states
  const [selectedTicket, setSelectedTicket] = useState<ITicketSoporteCorporativo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Export modal states
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportType, setExportType] = useState<'selected' | 'all' | 'filtered'>('all');
  const [exportDateRange, setExportDateRange] = useState<Date[]>([]);
  const [exportNumeroTicket, setExportNumeroTicket] = useState('');
  const exportFlatpickrRef = useRef<Flatpickr | null>(null);

  // Finalizar modal states
  const [isFinalizarModalOpen, setIsFinalizarModalOpen] = useState(false);
  const [ticketToFinalize, setTicketToFinalize] = useState<ITicketSoporteCorporativo | null>(null);
  const [finalizarFechaEjecucion, setFinalizarFechaEjecucion] = useState<Date | null>(null);
  const [finalizarComentarios, setFinalizarComentarios] = useState('');
  const [finalizarFiles, setFinalizarFiles] = useState<File[]>([]);
  const finalizarFlatpickrRef = useRef<Flatpickr | null>(null);

  // Rechazar modal states
  const [isRechazarModalOpen, setIsRechazarModalOpen] = useState(false);
  const [ticketToReject, setTicketToReject] = useState<ITicketSoporteCorporativo | null>(null);
  const [rechazarMotivo, setRechazarMotivo] = useState('');

  // Correos en copia modal states
  const [isCorreosModalOpen, setIsCorreosModalOpen] = useState(false);
  const [ticketForCorreos, setTicketForCorreos] = useState<ITicketSoporteCorporativo | null>(null);
  const [correosCopia, setCorreosCopia] = useState<string[]>([]);
  const [nuevoCorreo, setNuevoCorreo] = useState('');

  // Correos predeterminados modal states
  const [isPredeterminadosModalOpen, setIsPredeterminadosModalOpen] = useState(false);
  const [correosPredeterminados, setCorreosPredeterminados] = useState<string[]>([]);
  const [nuevoCorreoPredeterminado, setNuevoCorreoPredeterminado] = useState('');

  // Row selection state
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  // Audit modal states
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [auditPagination, setAuditPagination] = useState({ page: 0, pageSize: 10 });
  const [auditFilters, setAuditFilters] = useState<{
    tipo_accion?: string;
    fecha_after?: string;
    fecha_before?: string;
  }>({});

  // Audit details modal states
  const [isAuditDetailsModalOpen, setIsAuditDetailsModalOpen] = useState(false);
  const [selectedAuditDetails, setSelectedAuditDetails] = useState<Record<string, unknown> | null>(null);
  const [selectedAuditAction, setSelectedAuditAction] = useState<string>('');

  // Permission check for audit button
  const hasAuditPermission = hasPermission(PermissionsEnum.api_view_auditoriaticketsoportecorporativo);

  // Al cambiar los filtros por columna, volver a la primera página
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [filterObject]);

  // Fetch data from API
  const { data: ticketsResponse, isLoading, isRefetching, refetch } = useFetchTicketsSoporteCorporativo({
    enabled: true,
    params: {
      page: pagination.pageIndex + 1,
      page_size: pagination.pageSize,
      ...searchParams,
      ...filterObject,
    },
  });

  // Fetch audit data
  const { data: auditResponse, isLoading: isAuditLoading } = useFetchAuditoriaTicketSoporte({
    enabled: isAuditModalOpen && hasAuditPermission,
    params: {
      page: auditPagination.page + 1,
      page_size: auditPagination.pageSize,
      ...auditFilters,
    },
  });

  // Upload and export mutations
  const uploadMutation = useUploadDocumentosTicket();
  const exportMutation = useExportTicketsToExcel();
  const updateMutation = useUpdateTicketSoporteCorporativo({
    navigate,
    enableRedirect: false,
    enableSuccessAlert: true,
    customSuccessMessage: 'Ticket finalizado correctamente',
  });
  const rechazarMutation = useRechazarTicketSoporteCorporativo();
  const correosMutation = useUpdateCorreosCopiaTicket();
  const predeterminadosMutation = useUpdateCorreosPredeterminados();

  // Fetch correos predeterminados
  const { data: predeterminadosResponse, refetch: refetchPredeterminados } = useFetchCorreosPredeterminados(true);

  const ticketsData = ticketsResponse?.data?.data || [];
  const auditData = auditResponse?.data?.data || [];
  const auditTotalCount = auditResponse?.data?.count || 0;

  // Función para sumar capacidades y formatear
  const sumarCapacidades = (capacidad: string | null, bonificacion: string | null): string => {
    if (!capacidad && !bonificacion) return '-';
    if (!bonificacion) return capacidad || '-';
    if (!capacidad) return bonificacion || '-';

    // Extraer números de las cadenas (ej: "100 Mbps" -> 100)
    const capMatch = capacidad.match(/(\d+(?:\.\d+)?)/);
    const bonifMatch = bonificacion.match(/(\d+(?:\.\d+)?)/);

    if (capMatch && bonifMatch) {
      const total = parseFloat(capMatch[1]) + parseFloat(bonifMatch[1]);
      // Obtener unidad de la capacidad principal
      const unidad = capacidad.replace(capMatch[1], '').trim();
      const totalFormateado = Number.isInteger(total) ? total : total.toFixed(1);
      return `${totalFormateado} ${unidad} (${capacidad} + ${bonificacion})`;
    } else {
      return `${capacidad} + ${bonificacion}`;
    }
  };
  const totalCount = ticketsResponse?.data?.count || 0;

  const handleDateChange = (selectedDates: Date[]) => {
    setDateRange(selectedDates);
  };

  const handleSearch = () => {
    const params: typeof searchParams = {};

    if (dateRange.length === 2) {
      params.fecha_ejecucion_after = dateRange[0].toISOString().split('T')[0];
      params.fecha_ejecucion_before = dateRange[1].toISOString().split('T')[0];
    }

    if (numeroTicket) {
      params.numero_referencia = numeroTicket;
    }

    setSearchParams(params);
    setPagination({ ...pagination, pageIndex: 0 });
  };

  const handleClearFilters = () => {
    setDateRange([]);
    setNumeroTicket('');
    setSearchParams({});
    setColumnFilters([] as any);
    setPagination({ ...pagination, pageIndex: 0 });
    if (flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.clear();
    }
  };

  // Check if there are active filters
  const hasActiveFilters =
    dateRange.length > 0 || numeroTicket !== '' || Object.keys(filterObject).length > 0;

  const handleExportExcel = () => {
    setIsExportModalOpen(true);
  };

  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
    setExportType('all');
    setExportDateRange([]);
    setExportNumeroTicket('');
  };

  const handleExportSubmit = async () => {
    const params: {
      ticket_ids?: string[];
      fecha_inicio?: string;
      fecha_fin?: string;
      numero_referencia?: string;
    } = {};

    if (exportType === 'selected') {
      const selectedIds = Object.keys(rowSelection)
        .filter((key) => rowSelection[key])
        .map((index) => ticketsData[parseInt(index)]?.id_ticket_soporte)
        .filter(Boolean);

      if (selectedIds.length === 0) {
        alert('Por favor selecciona al menos un ticket');
        return;
      }
      params.ticket_ids = selectedIds;
    } else if (exportType === 'filtered') {
      if (exportDateRange.length === 2) {
        params.fecha_inicio = exportDateRange[0].toISOString().split('T')[0];
        params.fecha_fin = exportDateRange[1].toISOString().split('T')[0];
      }
      if (exportNumeroTicket) {
        params.numero_referencia = exportNumeroTicket;
      }
    }

    try {
      await exportMutation.mutateAsync(params);
      handleCloseExportModal();
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  // Get count of selected rows
  const selectedCount = Object.keys(rowSelection).filter((key) => rowSelection[key]).length;

  // Modal handlers
  const handleOpenModal = (ticket: ITicketSoporteCorporativo) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
    setPreviewImage(null);
  };

  // Audit modal handlers
  const handleOpenAuditModal = () => {
    setIsAuditModalOpen(true);
    setAuditPagination({ page: 0, pageSize: 10 });
    setAuditFilters({});
  };

  const handleCloseAuditModal = () => {
    setIsAuditModalOpen(false);
  };

  // Audit details modal handlers
  const handleOpenAuditDetailsModal = (detalles: Record<string, unknown>, tipoAccion: string) => {
    setSelectedAuditDetails(detalles);
    setSelectedAuditAction(tipoAccion);
    setIsAuditDetailsModalOpen(true);
  };

  const handleCloseAuditDetailsModal = () => {
    setIsAuditDetailsModalOpen(false);
    setSelectedAuditDetails(null);
    setSelectedAuditAction('');
  };

  const handleAuditPageChange = (_event: unknown, newPage: number) => {
    setAuditPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleAuditRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAuditPagination({ page: 0, pageSize: parseInt(event.target.value, 10) });
  };

  // Finalizar modal handlers
  const handleOpenFinalizarModal = (ticket: ITicketSoporteCorporativo) => {
    setTicketToFinalize(ticket);
    setFinalizarFechaEjecucion(null);
    setFinalizarComentarios('');
    setFinalizarFiles([]);
    setIsFinalizarModalOpen(true);
  };

  const handleCloseFinalizarModal = () => {
    setIsFinalizarModalOpen(false);
    setTicketToFinalize(null);
    setFinalizarFechaEjecucion(null);
    setFinalizarComentarios('');
    setFinalizarFiles([]);
  };

  const handleFinalizarFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setFinalizarFiles((prev) => [...prev, ...Array.from(files)]);
    if (finalizarFileInputRef.current) {
      finalizarFileInputRef.current.value = '';
    }
  };

  const handleRemoveFinalizarFile = (index: number) => {
    setFinalizarFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFinalizarSubmit = async () => {
    if (!ticketToFinalize) return;

    if (!finalizarFechaEjecucion) {
      alert('Por favor selecciona una fecha de ejecución');
      return;
    }

    try {
      // Primero subir los archivos si hay
      if (finalizarFiles.length > 0) {
        await uploadMutation.mutateAsync({
          ticketId: ticketToFinalize.id_ticket_soporte,
          files: finalizarFiles,
        });
      }

      // Luego actualizar el ticket con la fecha, comentarios y estado
      const fechaRegistroSistema = new Date().toISOString();
      await updateMutation.mutateAsync({
        id: ticketToFinalize.id_ticket_soporte,
        data: {
          fecha_ejecucion: formatDateToYYYYMMDD(finalizarFechaEjecucion),
          fecha_registro_sistema: fechaRegistroSistema,
          comentarios: finalizarComentarios || null,
          estado_ticket: 'Finalizado',
        },
      });

      handleCloseFinalizarModal();
    } catch (error) {
      console.error('Error al finalizar ticket:', error);
    }
  };

  // Rechazar modal handlers
  const handleOpenRechazarModal = (ticket: ITicketSoporteCorporativo) => {
    setTicketToReject(ticket);
    setRechazarMotivo('');
    setIsRechazarModalOpen(true);
  };

  const handleCloseRechazarModal = () => {
    setIsRechazarModalOpen(false);
    setTicketToReject(null);
    setRechazarMotivo('');
  };

  const handleRechazarSubmit = async () => {
    if (!ticketToReject) return;

    if (!rechazarMotivo.trim()) {
      alert('Por favor ingresa el motivo del rechazo');
      return;
    }

    try {
      await rechazarMutation.mutateAsync({
        ticketId: ticketToReject.id_ticket_soporte,
        motivo_rechazo: rechazarMotivo,
      });

      handleCloseRechazarModal();
    } catch (error) {
      console.error('Error al rechazar ticket:', error);
    }
  };

  // Correos en copia modal handlers
  const handleOpenCorreosModal = (ticket: ITicketSoporteCorporativo) => {
    setTicketForCorreos(ticket);

    // Cargar correos predeterminados + correos adicionales del ticket
    const defaultEmails = predeterminadosResponse?.data?.correos_predeterminados || [];
    const ticketEmails = ticket.correos_copia || [];

    // Combinar ambos arrays sin duplicados, manteniendo predeterminados primero
    const allEmails = [...defaultEmails];
    ticketEmails.forEach(email => {
      if (!allEmails.includes(email)) {
        allEmails.push(email);
      }
    });

    setCorreosCopia(allEmails);
    setNuevoCorreo('');
    setIsCorreosModalOpen(true);
  };

  const handleCloseCorreosModal = () => {
    setIsCorreosModalOpen(false);
    setTicketForCorreos(null);
    setCorreosCopia([]);
    setNuevoCorreo('');
  };

  const handleAddCorreo = () => {
    const correo = nuevoCorreo.trim().toLowerCase();
    if (!correo) return;

    // Validar formato de correo
    const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
    if (!emailRegex.test(correo)) {
      alert('Por favor ingresa un correo electrónico válido');
      return;
    }

    // Verificar que no esté duplicado
    if (correosCopia.includes(correo)) {
      alert('Este correo ya está en la lista');
      return;
    }

    setCorreosCopia((prev) => [...prev, correo]);
    setNuevoCorreo('');
  };

  const handleRemoveCorreo = (correo: string) => {
    // Verificar si es un correo predeterminado (no se puede eliminar desde el modal de ticket)
    const defaultEmails = predeterminadosResponse?.data?.correos_predeterminados || [];
    if (defaultEmails.includes(correo)) {
      alert('Los correos predeterminados no pueden ser eliminados desde aquí. Solo los supervisores pueden editarlos desde el botón "Correos Predeterminados".');
      return;
    }

    setCorreosCopia((prev) => prev.filter((c) => c !== correo));
  };

  const handleCorreosSubmit = async () => {
    if (!ticketForCorreos) return;

    try {
      // Filtrar solo los correos adicionales (excluir los predeterminados)
      const defaultEmails = predeterminadosResponse?.data?.correos_predeterminados || [];
      const additionalEmails = correosCopia.filter(email => !defaultEmails.includes(email));

      await correosMutation.mutateAsync({
        ticketId: ticketForCorreos.id_ticket_soporte,
        correos_copia: additionalEmails,
      });

      handleCloseCorreosModal();
      refetch();
    } catch (error) {
      console.error('Error al actualizar correos en copia:', error);
    }
  };

  // Correos predeterminados modal handlers
  const handleOpenPredeterminadosModal = () => {
    const currentDefaults = predeterminadosResponse?.data?.correos_predeterminados || [];
    setCorreosPredeterminados(currentDefaults);
    setNuevoCorreoPredeterminado('');
    setIsPredeterminadosModalOpen(true);
  };

  const handleClosePredeterminadosModal = () => {
    setIsPredeterminadosModalOpen(false);
    setCorreosPredeterminados([]);
    setNuevoCorreoPredeterminado('');
  };

  const handleAddCorreoPredeterminado = () => {
    const correo = nuevoCorreoPredeterminado.trim().toLowerCase();
    if (!correo) return;

    // Validar formato de correo
    const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
    if (!emailRegex.test(correo)) {
      alert('Por favor ingresa un correo electrónico válido');
      return;
    }

    // Verificar que no esté duplicado
    if (correosPredeterminados.includes(correo)) {
      alert('Este correo ya está en la lista de predeterminados');
      return;
    }

    setCorreosPredeterminados((prev) => [...prev, correo]);
    setNuevoCorreoPredeterminado('');
  };

  const handleRemoveCorreoPredeterminado = (correo: string) => {
    setCorreosPredeterminados((prev) => prev.filter((c) => c !== correo));
  };

  const handlePredeterminadosSubmit = async () => {
    try {
      await predeterminadosMutation.mutateAsync({
        correos_predeterminados: correosPredeterminados,
      });

      handleClosePredeterminadosModal();
      refetchPredeterminados();
    } catch (error) {
      console.error('Error al actualizar correos predeterminados:', error);
    }
  };

  // File upload handler
  // Download handler
  const handleDownload = (doc: IDocumentoTicket) => {
    window.open(doc.url, '_blank');
  };

  // Definición de columnas usando MRT_ColumnDef
  const columns = useMemo<MRT_ColumnDef<ITicketSoporteCorporativo>[]>(
    () => [
      {
        accessorKey: 'numero_referencia',
        header: 'N° Ticket',
        size: 100,
      },
      {
        accessorKey: 'ruc',
        header: 'RUC',
        size: 140,
      },
      {
        accessorKey: 'numero_contrato',
        header: 'N° Contrato',
        size: 120,
      },
      {
        accessorKey: 'razon_social',
        header: 'Razón Social',
        size: 180,
      },
      {
        accessorKey: 'nombre_enlace',
        header: 'Nombre Enlace',
        size: 180,
      },
      {
        accessorKey: 'tipo_ticket',
        header: 'Tipo',
        size: 130,
        filterVariant: 'select',
        filterSelectOptions: ['Upgrade', 'Downgrade', 'Cancelacion', 'Precio'],
        Cell: ({ row }) => {
          const tipoTicket = row.original.tipo_ticket;
          const tipoConfig: Record<string, { bg: string; color: string; icon: React.ReactNode }> = {
            Upgrade: { bg: '#e8f5e9', color: '#2e7d32', icon: <ArrowUpward sx={{ fontSize: 18 }} /> },
            Downgrade: { bg: '#ffebee', color: '#c62828', icon: <ArrowDownward sx={{ fontSize: 18 }} /> },
            Cancelacion: { bg: '#fff3e0', color: '#e65100', icon: <Close sx={{ fontSize: 18 }} /> },
            Precio: { bg: '#e3f2fd', color: '#1565c0', icon: <AttachMoney sx={{ fontSize: 18 }} /> },
          };
          const config = tipoConfig[tipoTicket] || tipoConfig.Upgrade;
          return (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: '16px',
                backgroundColor: config.bg,
                color: config.color,
                fontWeight: 600,
                fontSize: '0.85rem',
              }}
            >
              {config.icon}
              {tipoTicket}
            </Box>
          );
        },
      },
      {
        accessorKey: 'fecha_ejecucion',
        header: 'Fecha Ejecución',
        size: 130,
        enableColumnFilter: false, // se filtra por el rango de fechas del panel superior
        Cell: ({ row }) => formatDate(row.original.fecha_ejecucion),
      },
      {
        accessorKey: 'estado_ticket',
        header: 'Estado',
        size: 130,
        filterVariant: 'select',
        filterSelectOptions: ['Pendiente', 'Finalizado', 'Rechazado'],
        Cell: ({ row }) => {
          const estado = row.original.estado_ticket;
          const getEstadoStyles = () => {
            switch (estado) {
              case 'Finalizado':
                return { bg: '#e8f5e9', color: '#2e7d32', icon: <CheckCircle sx={{ fontSize: 18 }} /> };
              case 'Rechazado':
                return { bg: '#ffebee', color: '#c62828', icon: <Cancel sx={{ fontSize: 18 }} /> };
              default:
                return { bg: '#fff3e0', color: '#e65100', icon: <Schedule sx={{ fontSize: 18 }} /> };
            }
          };
          const styles = getEstadoStyles();
          return (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: '16px',
                backgroundColor: styles.bg,
                color: styles.color,
                fontWeight: 600,
                fontSize: '0.85rem',
              }}
            >
              {styles.icon}
              {estado}
            </Box>
          );
        },
      },
      {
        accessorKey: 'gestionado_por_username',
        header: 'Gestionado por',
        size: 130,
        enableColumnFilter: false, // campo calculado, sin filtro en el backend
        Cell: ({ row }) => row.original.gestionado_por_username || '-',
      },
      {
        accessorKey: 'tipo_cliente',
        header: 'Tipo Cliente',
        size: 120,
      },
      {
        id: 'documentos_count',
        header: 'Docs',
        size: 80,
        enableColumnFilter: false, // campo calculado, sin filtro en el backend
        Cell: ({ row }) => {
          const count = row.original.documentos?.length || 0;
          return (
            <Chip
              label={count}
              size="small"
              color={count > 0 ? 'primary' : 'default'}
              variant={count > 0 ? 'filled' : 'outlined'}
            />
          );
        },
      },
    ],
    []
  );

  const validarIPv4 = (ip: string): boolean => {
    const regex =
      /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

    return regex.test(ip);
  };

  // Botones de acción personalizados
  const renderCustomActions = (row: ITicketSoporteCorporativo) => {
    return (
      <>
        <Tooltip title="Ver Detalles" placement="top">
          <IconButton color="primary" onClick={() => handleOpenModal(row)}>
            <Visibility />
          </IconButton>
        </Tooltip>
        {row.estado_ticket === 'Pendiente' && (
          <>
            <Tooltip title="Gestionar Correos en Copia" placement="top">
              <IconButton
                sx={{ color: '#5c6bc0' }}
                onClick={() => handleOpenCorreosModal(row)}
              >
                <Email />
              </IconButton>
            </Tooltip>
            <Tooltip title="Finalizar (Aprobar)" placement="top">
              <IconButton
                color="success"
                onClick={() => handleOpenFinalizarModal(row)}
              >
                <CheckCircle />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rechazar" placement="top">
              <IconButton
                color="error"
                onClick={() => handleOpenRechazarModal(row)}
              >
                <Cancel />
              </IconButton>
            </Tooltip>
          </>
        )}
      </>
    );
  };

  return (
    <SingleTableBoxScene title="Gestión de Tickets">
      {/* Filtros */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4} md={3}>
          <DateRangePicker
            flatpickrRef={flatpickrRef}
            onChange={handleDateChange}
            label="Rango de Fechas"
            size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
          />
        </Grid>

        <Grid item xs={12} sm={4} md={3}>
          <TextField
            fullWidth
            label="Número de ticket"
            variant="outlined"
            size="small"
            value={numeroTicket}
            onChange={(e) => setNumeroTicket(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '53px',
                borderRadius: '9px',
              },
            }}
          />
        </Grid>

        <Grid item xs={6} sm={2} md={2}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<Search />}
            onClick={handleSearch}
            sx={{
              height: '53px',
              backgroundColor: '#1976D2',
              '&:hover': { backgroundColor: '#1565C0' },
            }}
          >
            Buscar
          </Button>
        </Grid>

        <Grid item xs={6} sm={2} md={2}>
          <Tooltip title="Limpiar filtros">
            <span>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterAltOff />}
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                sx={{
                  height: '53px',
                  borderColor: '#757575',
                  color: '#757575',
                  '&:hover': {
                    borderColor: '#424242',
                    backgroundColor: 'rgba(117, 117, 117, 0.04)',
                  },
                  '&.Mui-disabled': {
                    borderColor: '#e0e0e0',
                    color: '#bdbdbd',
                  },
                }}
              >
                Limpiar
              </Button>
            </span>
          </Tooltip>
        </Grid>

        <Grid item xs={6} sm={2} md={2}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={handleExportExcel}
            sx={{
              height: '53px',
              borderColor: '#4CAF50',
              color: '#4CAF50',
              '&:hover': {
                borderColor: '#388E3C',
                backgroundColor: 'rgba(76, 175, 80, 0.04)',
              },
            }}
          >
            Exportar Excel
          </Button>
        </Grid>
      </Grid>

      {/* Tabla */}
      <GeneralTable<ITicketSoporteCorporativo>
        columns={columns}
        data={ticketsData}
        isLoading={isLoading}
        isRefetching={isRefetching}
        enableGlobalFilter={false}
        enableColumnFilters={true}
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        columnFilterDisplayMode="subheader"
        enableSorting={false}
        pagination={pagination}
        onPaging={setPagination}
        rowCount={totalCount}
        enableColumnActions={true}
        enableEditing={true}
        positionActionsColumn="first"
        actionsColumnSize={150}
        showCustomActionBtnSpace={true}
        customActionBtnSpace={(row) => renderCustomActions(row)}
        canEdit={false}
        canDelete={false}
        density="comfortable"
        enableRowSelection={true}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
      />

      {/* Botones de Auditoría y Correos Predeterminados - Solo visible con permiso */}
      {hasAuditPermission && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Email />}
            onClick={handleOpenPredeterminadosModal}
            sx={{
              borderColor: '#f57c00',
              color: '#f57c00',
              '&:hover': {
                borderColor: '#ef6c00',
                backgroundColor: 'rgba(245, 124, 0, 0.04)',
              },
            }}
          >
            Correos Predeterminados
          </Button>
          <Button
            variant="outlined"
            startIcon={<History />}
            onClick={handleOpenAuditModal}
            sx={{
              borderColor: '#9c27b0',
              color: '#9c27b0',
              '&:hover': {
                borderColor: '#7b1fa2',
                backgroundColor: 'rgba(156, 39, 176, 0.04)',
              },
            }}
          >
            Auditoría
          </Button>
        </Box>
      )}

      {/* Modal de Detalles */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'white',
          }}
        >
          <Typography variant="h6">
            Ticket #{selectedTicket?.numero_referencia}
          </Typography>
          <IconButton onClick={handleCloseModal} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {selectedTicket && (
            <Grid container spacing={3}>
              {/* Información del Ticket */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Información del Cliente
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    RUC: <strong>{selectedTicket.ruc || '-'}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Razón Social: <strong>{selectedTicket.razon_social || '-'}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    N° Contrato: <strong>{selectedTicket.numero_contrato || '-'}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Nombre Enlace: <strong>{selectedTicket.nombre_enlace || '-'}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tipo Cliente: <strong>{selectedTicket.tipo_cliente}</strong>
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Detalles del Ticket
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">Tipo:</Typography>
                    {(() => {
                      const tipoTicket = selectedTicket.tipo_ticket;
                      const tipoConfig: Record<string, { bg: string; color: string; icon: React.ReactNode }> = {
                        Upgrade: { bg: '#e8f5e9', color: '#2e7d32', icon: <ArrowUpward sx={{ fontSize: 16 }} /> },
                        Downgrade: { bg: '#ffebee', color: '#c62828', icon: <ArrowDownward sx={{ fontSize: 16 }} /> },
                        Cancelacion: { bg: '#fff3e0', color: '#e65100', icon: <Close sx={{ fontSize: 16 }} /> },
                        Precio: { bg: '#e3f2fd', color: '#1565c0', icon: <AttachMoney sx={{ fontSize: 16 }} /> },
                      };
                      const config = tipoConfig[tipoTicket] || tipoConfig.Upgrade;
                      return (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '16px',
                            backgroundColor: config.bg,
                            color: config.color,
                            fontWeight: 600,
                            fontSize: '0.85rem',
                          }}
                        >
                          {config.icon}
                          {tipoTicket}
                        </Box>
                      );
                    })()}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">Estado:</Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '16px',
                        backgroundColor: selectedTicket.estado_ticket === 'Finalizado'
                          ? '#e8f5e9'
                          : selectedTicket.estado_ticket === 'Rechazado'
                            ? '#ffebee'
                            : '#fff3e0',
                        color: selectedTicket.estado_ticket === 'Finalizado'
                          ? '#2e7d32'
                          : selectedTicket.estado_ticket === 'Rechazado'
                            ? '#c62828'
                            : '#e65100',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                      }}
                    >
                      {selectedTicket.estado_ticket === 'Finalizado' ? (
                        <CheckCircle sx={{ fontSize: 16 }} />
                      ) : selectedTicket.estado_ticket === 'Rechazado' ? (
                        <Cancel sx={{ fontSize: 16 }} />
                      ) : (
                        <Schedule sx={{ fontSize: 16 }} />
                      )}
                      {selectedTicket.estado_ticket}
                    </Box>
                  </Box>

                  {/* Capacidades con desglose */}
                  <Divider sx={{ my: 1.5 }} />

                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Capacidades
                  </Typography>
                  <Box sx={{ pl: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Capacidad Actual: <strong>{selectedTicket.capacidad_actual || '-'}</strong>
                    </Typography>
                    {selectedTicket.bonificacion_actual && (
                      <Typography variant="body2" color="text.secondary">
                        Bonificación Actual: <strong>{selectedTicket.bonificacion_actual}</strong>
                      </Typography>
                    )}
                    {(selectedTicket.capacidad_actual || selectedTicket.bonificacion_actual) && (
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                        Total Actual: {sumarCapacidades(selectedTicket.capacidad_actual, selectedTicket.bonificacion_actual)}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ pl: 1, mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Capacidad Nueva: <strong>{selectedTicket.capacidad_nueva || '-'}</strong>
                    </Typography>
                    {selectedTicket.bonificacion_nueva && (
                      <Typography variant="body2" color="text.secondary">
                        Bonificación Nueva: <strong>{selectedTicket.bonificacion_nueva}</strong>
                      </Typography>
                    )}
                    {(selectedTicket.capacidad_nueva || selectedTicket.bonificacion_nueva) && (
                      <Typography variant="body2" color="success.dark" sx={{ fontWeight: 'bold' }}>
                        Total Nueva: {sumarCapacidades(selectedTicket.capacidad_nueva, selectedTicket.bonificacion_nueva)}
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: 1.5 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={selectedTicket.tipo_ticket === 'Precio' ? { fontWeight: 'bold', color: '#1565c0' } : {}}
                  >
                    Valor Actual: <strong>${selectedTicket.valor_actual}</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={selectedTicket.tipo_ticket === 'Precio' ? { fontWeight: 'bold', color: '#1565c0' } : {}}
                  >
                    Valor Nuevo: <strong>${selectedTicket.valor_nuevo}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fecha Ejecución: <strong>{formatDate(selectedTicket.fecha_ejecucion)}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fecha solicitud asesor: <strong>{selectedTicket.fecha_sol_asesor || '-'}</strong>
                  </Typography>

                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Contactos
                </Typography>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Contacto Técnico: <strong>{selectedTicket.contacto_tecnico || '-'}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Correo Técnico: <strong>{selectedTicket.correo_tecnico || '-'}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Asesor Comercial: <strong>{selectedTicket.asesor_comercial || '-'}</strong>
                  </Typography>
                </Box>

              </Grid>

              {/* Detalles de Rechazo - Solo visible cuando el ticket está Rechazado */}
              {selectedTicket.estado_ticket === 'Rechazado' && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: '#ffebee', p: 2, borderRadius: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#c62828' }}>
                      Detalles del Rechazo
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Fecha de Rechazo: <strong>{formatDateTime(selectedTicket.fecha_rechazo)}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Gestionado por: <strong>{selectedTicket.gestionado_por_username || '-'}</strong>
                      </Typography>
                    </Box>
                    {selectedTicket.motivo_rechazo && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          Motivo del Rechazo:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedTicket.motivo_rechazo}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Detalles de Gestión - Solo visible cuando el ticket está Finalizado */}
              {selectedTicket.estado_ticket === 'Finalizado' && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: '#e8f5e9', p: 2, borderRadius: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#2e7d32' }}>
                      Detalles de Gestión
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Fecha de Ejecución: <strong>{formatDate(selectedTicket.fecha_ejecucion)}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Fecha de registro en sistema: <strong>{formatDateTime(selectedTicket.fecha_registro_sistema)}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Gestionado por: <strong>{selectedTicket.gestionado_por_username || '-'}</strong>
                      </Typography>
                    </Box>
                    {selectedTicket.comentarios && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          Comentarios:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedTicket.comentarios}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Documentos - Solo lectura en estado Finalizado */}
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Documentos Adjuntos
                  </Typography>
                  {(!selectedTicket.documentos || selectedTicket.documentos.length === 0) ? (
                    <Box
                      sx={{
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      <InsertDriveFile sx={{ fontSize: 40, opacity: 0.5 }} />
                      <Typography variant="body2">No hay documentos adjuntos</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      <Grid container spacing={2}>
                        {selectedTicket.documentos.map((doc, index) => (
                          <Grid item xs={12} sm={6} key={index}>
                            <Card variant="outlined" sx={{ height: '100%' }}>
                              {doc.tipo === 'image' ? (
                                <CardMedia
                                  component="img"
                                  height="100"
                                  image={doc.url}
                                  alt={doc.nombre}
                                  sx={{ objectFit: 'cover', cursor: 'pointer' }}
                                  onClick={() => setPreviewImage(doc.url)}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    height: 100,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: '#f5f5f5',
                                  }}
                                >
                                  {getDocIcon(doc.tipo)}
                                </Box>
                              )}
                              <CardContent sx={{ py: 1 }}>
                                <Tooltip title={doc.nombre}>
                                  <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                    {doc.nombre}
                                  </Typography>
                                </Tooltip>
                                <Typography variant="caption" color="text.secondary">
                                  {formatFileSize(doc.size)} • {doc.extension.toUpperCase()}
                                </Typography>
                              </CardContent>
                              <CardActions sx={{ pt: 0 }}>
                                <Tooltip title="Descargar">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleDownload(doc)}
                                  >
                                    <Download fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </CardActions>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
      >
        <DialogContent sx={{ p: 0 }}>
          {previewImage && (
            <img
              src={previewImage}
              alt="Preview"
              style={{ maxWidth: '100%', maxHeight: '80vh' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewImage(null)}>Cerrar</Button>
          {previewImage && (
            <Button
              startIcon={<Download />}
              onClick={() => window.open(previewImage, '_blank')}
            >
              Descargar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Export Modal */}
      <Dialog
        open={isExportModalOpen}
        onClose={handleCloseExportModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#4CAF50',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileDownload />
            <Typography variant="h6">Exportar a Excel</Typography>
          </Box>
          <IconButton onClick={handleCloseExportModal} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Seleccionar tipo de exportación
            </Typography>
            <RadioGroup
              value={exportType}
              onChange={(e) => setExportType(e.target.value as 'selected' | 'all' | 'filtered')}
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label="Exportar todos los tickets"
              />
              <FormControlLabel
                value="selected"
                control={<Radio />}
                label={`Exportar tickets seleccionados (${selectedCount} seleccionados)`}
                disabled={selectedCount === 0}
              />
              <FormControlLabel
                value="filtered"
                control={<Radio />}
                label="Exportar con filtros personalizados"
              />
            </RadioGroup>
          </FormControl>

          {exportType === 'filtered' && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Filtros de exportación
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <DateRangePicker
                    flatpickrRef={exportFlatpickrRef}
                    onChange={(dates) => setExportDateRange(dates)}
                    onClear={() => setExportDateRange([])}
                    label="Rango de Fechas"
                    size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Número de ticket"
                    variant="outlined"
                    size="small"
                    value={exportNumeroTicket}
                    onChange={(e) => setExportNumeroTicket(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '9px',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              El archivo Excel incluirá:
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li><Typography variant="caption">Información completa de cada ticket</Typography></li>
              <li><Typography variant="caption">Enlaces a documentos adjuntos</Typography></li>
              <li><Typography variant="caption">Fecha y hora de descarga</Typography></li>
              <li><Typography variant="caption">Usuario que realizó la exportación</Typography></li>
            </ul>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseExportModal} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleExportSubmit}
            variant="contained"
            color="success"
            startIcon={exportMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <FileDownload />}
            disabled={exportMutation.isPending || (exportType === 'selected' && selectedCount === 0)}
          >
            {exportMutation.isPending ? 'Exportando...' : 'Exportar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Registro de Gestión (Finalizar) */}
      <Dialog
        open={isFinalizarModalOpen}
        onClose={handleCloseFinalizarModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#4CAF50',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle />
            <Typography variant="h6">Registro de Gestión</Typography>
          </Box>
          <IconButton onClick={handleCloseFinalizarModal} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {ticketToFinalize && (
            <Grid container spacing={3}>
              {/* Info del ticket */}
              <Grid item xs={12}>
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Ticket #{ticketToFinalize.numero_referencia} - {ticketToFinalize.razon_social}
                  </Typography>
                </Box>
              </Grid>

              {/* Fecha de ejecución */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Fecha de Ejecución *
                </Typography>
                <Flatpickr
                  ref={finalizarFlatpickrRef}
                  value={finalizarFechaEjecucion || undefined}
                  onChange={(dates) => setFinalizarFechaEjecucion(dates[0] || null)}
                  options={{
                    dateFormat: 'd/m/Y',
                    locale: {
                      firstDayOfWeek: 1,
                    },
                  }}
                  render={({ defaultValue }, ref) => (
                    <TextField
                      inputRef={ref}
                      defaultValue={defaultValue}
                      fullWidth
                      size="small"
                      placeholder="Seleccionar fecha"
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '9px',
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Comentarios */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Comentarios
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Ingrese comentarios sobre la gestión realizada..."
                  value={finalizarComentarios}
                  onChange={(e) => setFinalizarComentarios(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '9px',
                    },
                  }}
                />
              </Grid>

              {/* Documentos */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Documentos Adjuntos
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    onClick={() => finalizarFileInputRef.current?.click()}
                    size="small"
                  >
                    Agregar Archivos
                  </Button>
                  <input
                    ref={finalizarFileInputRef}
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.svg,.pdf,.doc,.docx,.xls,.xlsx"
                    style={{ display: 'none' }}
                    onChange={handleFinalizarFileSelect}
                  />
                </Box>

                {finalizarFiles.length === 0 ? (
                  <Box
                    sx={{
                      border: '2px dashed #ccc',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    <CloudUpload sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
                    <Typography variant="body2">
                      Arrastra archivos aquí o haz clic en "Agregar Archivos"
                    </Typography>
                    <Typography variant="caption">
                      Formatos: imágenes, PDFs, Word, Excel
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {finalizarFiles.map((file, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1,
                          mb: 1,
                          bgcolor: '#f5f5f5',
                          borderRadius: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <InsertDriveFile sx={{ color: '#757575' }} />
                          <Box>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(file.size)}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveFinalizarFile(index)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseFinalizarModal} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleFinalizarSubmit}
            variant="contained"
            color="success"
            startIcon={
              (uploadMutation.isPending || updateMutation.isPending) ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CheckCircle />
              )
            }
            disabled={uploadMutation.isPending || updateMutation.isPending || !finalizarFechaEjecucion}
          >
            {(uploadMutation.isPending || updateMutation.isPending) ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Rechazar Ticket */}
      <Dialog
        open={isRechazarModalOpen}
        onClose={handleCloseRechazarModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#c62828',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Cancel />
            <Typography variant="h6">Rechazar Ticket</Typography>
          </Box>
          <IconButton onClick={handleCloseRechazarModal} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {ticketToReject && (
            <Grid container spacing={3}>
              {/* Info del ticket */}
              <Grid item xs={12}>
                <Box sx={{ bgcolor: '#ffebee', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Ticket #{ticketToReject.numero_referencia} - {ticketToReject.razon_social}
                  </Typography>
                </Box>
              </Grid>

              {/* Fecha del rechazo (automática del sistema) - solo lectura */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Fecha del Rechazo
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={new Date().toLocaleString('es-EC', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '9px',
                      bgcolor: '#f5f5f5',
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Fecha automática del sistema
                </Typography>
              </Grid>

              {/* Motivo del rechazo */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Motivo del Rechazo *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Ingrese el motivo del rechazo..."
                  value={rechazarMotivo}
                  onChange={(e) => setRechazarMotivo(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '9px',
                    },
                  }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseRechazarModal} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleRechazarSubmit}
            variant="contained"
            color="error"
            startIcon={
              rechazarMutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Cancel />
              )
            }
            disabled={rechazarMutation.isPending || !rechazarMotivo.trim()}
          >
            {rechazarMutation.isPending ? 'Rechazando...' : 'Rechazar Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Correos en Copia */}
      <Dialog
        open={isCorreosModalOpen}
        onClose={handleCloseCorreosModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#5c6bc0',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email />
            <Typography variant="h6">Gestionar Correos en Copia</Typography>
          </Box>
          <IconButton onClick={handleCloseCorreosModal} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {ticketForCorreos && (
            <Grid container spacing={3}>
              {/* Info del ticket */}
              <Grid item xs={12}>
                <Box sx={{ bgcolor: '#e8eaf6', p: 2, borderRadius: 1, mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Ticket #{ticketForCorreos.numero_referencia} - {ticketForCorreos.razon_social}
                  </Typography>
                </Box>
              </Grid>

              {/* Correo técnico principal (solo lectura) */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Correo Técnico Principal
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={ticketForCorreos.correo_tecnico || 'No especificado'}
                  InputProps={{
                    readOnly: true,
                    startAdornment: <Email sx={{ mr: 1, color: '#5c6bc0' }} />,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '9px',
                      bgcolor: '#f5f5f5',
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Este correo recibirá la notificación principal
                </Typography>
              </Grid>

              {/* Agregar nuevo correo */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
                  Correos Adicionales (CC)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="correo@ejemplo.com"
                    value={nuevoCorreo}
                    onChange={(e) => setNuevoCorreo(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCorreo()}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '9px',
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddCorreo}
                    sx={{ bgcolor: '#5c6bc0', '&:hover': { bgcolor: '#3f51b5' } }}
                  >
                    <Add />
                  </Button>
                </Box>
              </Grid>

              {/* Lista de correos en copia */}
              <Grid item xs={12}>
                {correosCopia.length > 0 ? (
                  <>
                    {/* Mostrar correos predeterminados primero */}
                    {correosCopia.filter(correo =>
                      (predeterminadosResponse?.data?.correos_predeterminados || []).includes(correo)
                    ).length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Correos Predeterminados (Configurados por el sistema)
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {correosCopia
                              .filter(correo => (predeterminadosResponse?.data?.correos_predeterminados || []).includes(correo))
                              .map((correo, index) => (
                                <Chip
                                  key={`default-${index}`}
                                  label={correo}
                                  onDelete={() => handleRemoveCorreo(correo)}
                                  icon={<Email sx={{ fontSize: '16px !important' }} />}
                                  sx={{
                                    bgcolor: '#fff3e0',
                                    borderColor: '#f57c00',
                                    border: '1px solid',
                                    '& .MuiChip-deleteIcon': {
                                      color: '#757575',
                                      '&:hover': { color: '#d32f2f' },
                                    },
                                  }}
                                />
                              ))}
                          </Box>
                        </Box>
                      )}

                    {/* Mostrar correos adicionales */}
                    {correosCopia.filter(correo =>
                      !(predeterminadosResponse?.data?.correos_predeterminados || []).includes(correo)
                    ).length > 0 && (
                        <Box>
                          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Correos Adicionales (Específicos de este ticket)
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {correosCopia
                              .filter(correo => !(predeterminadosResponse?.data?.correos_predeterminados || []).includes(correo))
                              .map((correo, index) => (
                                <Chip
                                  key={`additional-${index}`}
                                  label={correo}
                                  onDelete={() => handleRemoveCorreo(correo)}
                                  icon={<Email sx={{ fontSize: '16px !important' }} />}
                                  sx={{
                                    bgcolor: '#e8eaf6',
                                    '& .MuiChip-deleteIcon': {
                                      color: '#c62828',
                                      '&:hover': { color: '#d32f2f' },
                                    },
                                  }}
                                />
                              ))}
                          </Box>
                        </Box>
                      )}
                  </>
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No hay correos adicionales configurados.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Los correos agregados aquí recibirán copia de las notificaciones de aprobación/rechazo.
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseCorreosModal} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleCorreosSubmit}
            variant="contained"
            startIcon={
              correosMutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Done />
              )
            }
            disabled={correosMutation.isPending}
            sx={{ bgcolor: '#5c6bc0', '&:hover': { bgcolor: '#3f51b5' } }}
          >
            {correosMutation.isPending ? 'Guardando...' : 'Guardar Correos'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Correos Predeterminados (Solo Supervisores) */}
      <Dialog
        open={isPredeterminadosModalOpen}
        onClose={handleClosePredeterminadosModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#f57c00',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email />
            <Typography variant="h6">Configurar Correos Predeterminados</Typography>
          </Box>
          <IconButton onClick={handleClosePredeterminadosModal} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Información */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: 1, mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Los correos predeterminados se agregarán automáticamente como CC a todas las notificaciones de tickets.
                  Solo los supervisores pueden editar esta configuración.
                </Typography>
              </Box>
            </Grid>

            {/* Agregar nuevo correo predeterminado */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Agregar Correo Predeterminado
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="correo@fibramax.ec"
                  value={nuevoCorreoPredeterminado}
                  onChange={(e) => setNuevoCorreoPredeterminado(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCorreoPredeterminado()}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '9px',
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddCorreoPredeterminado}
                  sx={{ bgcolor: '#f57c00', '&:hover': { bgcolor: '#ef6c00' } }}
                >
                  <Add />
                </Button>
              </Box>
            </Grid>

            {/* Lista de correos predeterminados */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Correos Predeterminados Configurados
              </Typography>
              {correosPredeterminados.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {correosPredeterminados.map((correo, index) => (
                    <Chip
                      key={index}
                      label={correo}
                      onDelete={() => handleRemoveCorreoPredeterminado(correo)}
                      icon={<Email sx={{ fontSize: '16px !important' }} />}
                      sx={{
                        bgcolor: '#fff3e0',
                        '& .MuiChip-deleteIcon': {
                          color: '#c62828',
                          '&:hover': { color: '#d32f2f' },
                        },
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No hay correos predeterminados configurados.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Los correos que agregues aquí se enviarán automáticamente en copia a todas las notificaciones de tickets.
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClosePredeterminadosModal} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handlePredeterminadosSubmit}
            variant="contained"
            startIcon={
              predeterminadosMutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Done />
              )
            }
            disabled={predeterminadosMutation.isPending}
            sx={{ bgcolor: '#f57c00', '&:hover': { bgcolor: '#ef6c00' } }}
          >
            {predeterminadosMutation.isPending ? 'Guardando...' : 'Guardar Configuración'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Auditoría */}
      <Dialog
        open={isAuditModalOpen}
        onClose={handleCloseAuditModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, minHeight: '60vh' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#9c27b0',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History />
            <Typography variant="h6">Registro de Auditoría</Typography>
          </Box>
          <IconButton onClick={handleCloseAuditModal} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {/* Filtros de Auditoría */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de Acción</InputLabel>
                <Select
                  value={auditFilters.tipo_accion || ''}
                  label="Tipo de Acción"
                  onChange={(e) => {
                    setAuditFilters((prev) => ({
                      ...prev,
                      tipo_accion: e.target.value || undefined,
                    }));
                    setAuditPagination((prev) => ({ ...prev, page: 0 }));
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="CREACION">Creación</MenuItem>
                  <MenuItem value="FINALIZACION">Finalización</MenuItem>
                  <MenuItem value="EXPORTACION_EXCEL">Exportación Excel</MenuItem>
                  <MenuItem value="ENVIO_EMAIL">Envío Email</MenuItem>
                  <MenuItem value="SUBIDA_DOCUMENTO">Subida Documento</MenuItem>
                  <MenuItem value="ELIMINACION_DOCUMENTO">Eliminación Documento</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Tabla de Auditoría */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Fecha/Hora</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tipo de Acción</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>N° Ticket</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Usuario</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>IP</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Detalles</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isAuditLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={30} />
                      <Typography variant="body2" sx={{ mt: 1 }}>Cargando registros...</Typography>
                    </TableCell>
                  </TableRow>
                ) : auditData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <History sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No hay registros de auditoría
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  auditData.map((audit) => {
                    const actionConfig = AUDIT_ACTION_CONFIG[audit.tipo_accion];
                    return (
                      <TableRow key={audit.id_auditoria} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(audit.fecha_creacion)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={actionConfig?.label || audit.tipo_accion}
                            size="small"
                            sx={{
                              bgcolor: actionConfig?.bgColor || '#f5f5f5',
                              color: actionConfig?.color || '#666',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {audit.numero_ticket || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {audit.username || 'Sistema'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {audit.ip || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {audit.detalles && Object.keys(audit.detalles).length > 0 ? (
                            <Chip
                              label="Ver detalles"
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenAuditDetailsModal(audit.detalles, audit.tipo_accion)}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: 'rgba(156, 39, 176, 0.08)',
                                  borderColor: '#9c27b0',
                                },
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginación */}
          <TablePagination
            component="div"
            count={auditTotalCount}
            page={auditPagination.page}
            onPageChange={handleAuditPageChange}
            rowsPerPage={auditPagination.pageSize}
            onRowsPerPageChange={handleAuditRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Registros por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAuditModal} variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Detalles de Auditoría */}
      <Dialog
        open={isAuditDetailsModalOpen}
        onClose={handleCloseAuditDetailsModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#9c27b0',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility />
            <Typography variant="h6">Detalles de Auditoría</Typography>
          </Box>
          <IconButton onClick={handleCloseAuditDetailsModal} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {selectedAuditDetails && (
            <Box>
              {/* Tipo de acción */}
              <Box sx={{ mb: 2, p: 2, bgcolor: '#f3e5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Tipo de Acción
                </Typography>
                <Chip
                  label={AUDIT_ACTION_CONFIG[selectedAuditAction as TipoAccionAuditoria]?.label || selectedAuditAction}
                  size="small"
                  sx={{
                    bgcolor: AUDIT_ACTION_CONFIG[selectedAuditAction as TipoAccionAuditoria]?.bgColor || '#f5f5f5',
                    color: AUDIT_ACTION_CONFIG[selectedAuditAction as TipoAccionAuditoria]?.color || '#666',
                    fontWeight: 600,
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Detalles organizados */}
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                Información Detallada
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {Object.entries(selectedAuditDetails).map(([key, value]) => {
                  // Formatear la clave para mostrarla legible
                  const formattedKey = key
                    .replace(/_/g, ' ')
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');

                  // Formatear el valor según su tipo
                  let formattedValue: string;
                  let isJsonObject = false;

                  if (value === null || value === undefined) {
                    formattedValue = '-';
                  } else if (Array.isArray(value)) {
                    // Verificar primero si es array (antes de object)
                    formattedValue = value.length > 0 ? value.join(', ') : 'Sin elementos';
                  } else if (typeof value === 'object') {
                    // Luego verificar si es objeto
                    formattedValue = JSON.stringify(value, null, 2);
                    isJsonObject = true;
                  } else {
                    formattedValue = String(value);
                  }

                  return (
                    <Box
                      key={key}
                      sx={{
                        p: 1.5,
                        bgcolor: '#fafafa',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        {formattedKey}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: isJsonObject ? 'monospace' : 'inherit',
                          whiteSpace: isJsonObject ? 'pre-wrap' : 'normal',
                          wordBreak: 'break-word',
                        }}
                      >
                        {formattedValue}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAuditDetailsModal} variant="outlined" startIcon={<Close />}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </SingleTableBoxScene>
  );
}
